import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { Button } from "../components/ui/Button";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { EmptyState } from "../components/ui/EmptyState";
import { ErrorText } from "../components/ui/feedback";
import { Menu } from "../components/ui/Menu";
import { formatRelativeTime } from "../lib/time";
import { adminApi } from "./api";
import { QueryError } from "./dense";
import { InviteSoundGuyModal } from "./InviteSoundGuyModal";
import { ADMIN_KEY, adminInviteRequestsQuery } from "./queries";
import type { AdminInviteRequest } from "./types";

/** Messages longer than this start collapsed, with a toggle for the full text. */
const PREVIEW_CHARS = 120;

function RequestMessage({ message }: { message: string | null }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  if (!message) {
    return <span className="text-note text-ink-3">{t("admin.requests.noMessage")}</span>;
  }
  const long = message.length > PREVIEW_CHARS;
  const shown = long && !expanded ? `${message.slice(0, PREVIEW_CHARS).trimEnd()}…` : message;
  return (
    <div className="min-w-0 text-operate text-ink-2">
      <p className="break-words whitespace-pre-line">{shown}</p>
      {long ? (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          className="mt-1 min-h-11 text-note font-bold text-blue underline underline-offset-2 md:min-h-0"
        >
          {expanded ? t("admin.requests.hideMessage") : t("admin.requests.showMessage")}
        </button>
      ) : null}
    </div>
  );
}

/** Public invite requests still waiting for an admin. Each row can become an invite or be dismissed. */
export function InviteRequestsSection() {
  const { t, lang } = useI18n();
  const queryClient = useQueryClient();
  const requests = useQuery(adminInviteRequestsQuery("new"));
  const [inviting, setInviting] = useState<AdminInviteRequest | null>(null);
  const [dismissing, setDismissing] = useState<AdminInviteRequest | null>(null);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [...ADMIN_KEY, "invite-requests"] }),
      queryClient.invalidateQueries({ queryKey: [...ADMIN_KEY, "invites"] }),
    ]);
  const markInvited = useMutation({
    mutationFn: (id: string) => adminApi.markInviteRequestInvited(id),
    onSuccess: refresh,
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => adminApi.dismissInviteRequest(id),
    onSuccess: () => {
      setDismissing(null);
      return refresh();
    },
  });
  const busy = markInvited.isPending || dismiss.isPending;
  const rows = Array.isArray(requests.data) ? requests.data : [];

  return (
    <section aria-labelledby="invite-requests-heading" className="space-y-3">
      <h2
        id="invite-requests-heading"
        className="font-display text-lg font-bold tracking-[0.01em] text-ink uppercase"
      >
        {t("admin.requests.title")}
      </h2>
      <ErrorText error={markInvited.error ?? dismiss.error} />
      {requests.isError ? (
        <QueryError error={requests.error} onRetry={() => void requests.refetch()} />
      ) : null}
      {requests.isPending ? <p className="text-operate text-ink-3">{t("app.loading")}</p> : null}
      {requests.data && rows.length === 0 ? (
        <EmptyState>{t("admin.requests.empty")}</EmptyState>
      ) : null}

      {rows.length > 0 ? (
        <ul
          aria-label={t("admin.requests.title")}
          className="divide-y divide-hairline rounded-panel bg-board shadow-board md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,1.1fr)_minmax(0,1.8fr)_max-content_max-content] md:gap-x-3"
        >
          {rows.map((request) => (
            <li
              key={request.id}
              className="grid gap-3 px-3 py-3 md:col-span-full md:grid-cols-subgrid md:items-start"
            >
              <p className="min-w-0 break-words text-operate font-bold text-ink">{request.name}</p>
              <p className="min-w-0 break-words text-operate text-ink-2">
                <span className="text-note text-ink-3 md:hidden">
                  {t("admin.requests.col.team")}:{" "}
                </span>
                {request.team}
              </p>
              <p className="min-w-0 break-all text-note text-ink-2">{request.email}</p>
              <RequestMessage message={request.message} />
              <time
                dateTime={request.createdAt}
                title={request.createdAt}
                className="text-note tabular-nums text-ink-3"
              >
                {formatRelativeTime(request.createdAt, new Date(), lang)}
              </time>
              <div className="flex items-center gap-2 md:justify-end">
                <Button variant="primary" disabled={busy} onClick={() => setInviting(request)}>
                  {t("admin.requests.createInvite")}
                </Button>
                <Menu
                  label={t("roster.moreActions", { name: request.email })}
                  items={[
                    {
                      label: t("admin.requests.dismiss"),
                      tone: "danger",
                      disabled: busy,
                      onSelect: () => setDismissing(request),
                    },
                  ]}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {inviting ? (
        <InviteSoundGuyModal
          initialEmail={inviting.email}
          initialWorkspaceName={inviting.team}
          onCreated={() => markInvited.mutate(inviting.id)}
          onClose={() => setInviting(null)}
        />
      ) : null}
      {dismissing ? (
        <ConfirmDialog
          title={t("admin.requests.dismissTitle")}
          message={t("admin.requests.dismissMessage", { email: dismissing.email })}
          confirmLabel={t("admin.requests.dismiss")}
          variant="danger"
          busy={dismiss.isPending}
          error={dismiss.error}
          onConfirm={() => dismiss.mutate(dismissing.id)}
          onCancel={() => setDismissing(null)}
        />
      ) : null}
    </section>
  );
}
